<template>
  <div class="documentation-page container-fluid px-4 py-3">
    <!-- Header Banner -->
    <div class="row mb-3">
      <div class="col-12">
        <div class="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm border">
          <div>
            <h4 class="mb-1 text-primary fw-bold">
              <i class="fa fa-book-open me-2"></i>System Documentation & Architecture
            </h4>
            <p class="text-muted small mb-0">
              Interactive technical documentation, order system flows, strategy guidelines, and API references.
            </p>
          </div>
          <div class="d-flex align-items-center gap-2">
            <button class="btn btn-outline-secondary btn-sm" @click="fetchDocsList" :disabled="loadingList" title="Refresh documentation list">
              <i class="fa fa-rotate me-1" :class="{ 'fa-spin': loadingList }"></i>Refresh
            </button>
            <button class="btn btn-primary btn-sm d-lg-none" @click="sidebarDrawer = !sidebarDrawer">
              <i class="fa fa-bars me-1"></i>Select Doc
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Layout -->
    <div class="row g-3">
      <!-- Left Column: Document Navigation Sidebar -->
      <div class="col-12 col-lg-3" :class="{ 'd-none d-lg-block': !sidebarDrawer }">
        <div class="card shadow-sm border-0 sticky-top" style="top: 80px; z-index: 10;">
          <div class="card-header bg-light border-bottom py-2 px-3 d-flex justify-content-between align-items-center">
            <span class="fw-bold text-dark small"><i class="fa fa-folder-tree me-2 text-primary"></i>Documentation Index</span>
            <span class="badge bg-primary rounded-pill">{{ docsList.length }} Docs</span>
          </div>
          
          <!-- Search filter input -->
          <div class="p-2 border-bottom">
            <div class="input-group input-group-sm">
              <span class="input-group-text bg-white border-end-0"><i class="fa fa-search text-muted"></i></span>
              <input
                type="text"
                class="form-control border-start-0"
                placeholder="Filter documents..."
                v-model="searchQuery"
              />
              <button v-if="searchQuery" class="btn btn-outline-secondary border-start-0" type="button" @click="searchQuery = ''">
                <i class="fa fa-times"></i>
              </button>
            </div>
          </div>

          <!-- Document List Grouped by Category -->
          <div class="card-body p-0 overflow-auto" style="max-height: calc(100vh - 240px);">
            <div v-if="loadingList" class="text-center py-4 text-muted">
              <div class="spinner-border spinner-border-sm text-primary me-2"></div>
              <span class="small">Loading index...</span>
            </div>

            <div v-else-if="filteredGroupedDocs.length === 0" class="text-center py-4 text-muted small">
              <i class="fa fa-circle-question fa-2x mb-2 d-block text-secondary"></i>
              No documents match "{{ searchQuery }}"
            </div>

            <div v-else class="list-group list-group-flush">
              <div v-for="group in filteredGroupedDocs" :key="group.category" class="mb-2">
                <div class="px-3 py-1 bg-light text-uppercase fw-bold text-secondary" style="font-size: 0.725rem; letter-spacing: 0.5px;">
                  {{ group.category }}
                </div>
                <button
                  v-for="doc in group.docs"
                  :key="doc.id"
                  type="button"
                  class="list-group-item list-group-item-action d-flex align-items-start py-2 px-3 border-0"
                  :class="{ 'active-doc bg-primary text-white': activeDocId === doc.id }"
                  @click="selectDocument(doc.id)"
                >
                  <i class="fa me-2 mt-1" :class="[doc.icon || 'fa-file-lines', activeDocId === doc.id ? 'text-white' : 'text-primary']"></i>
                  <div class="flex-grow-1 overflow-hidden">
                    <div class="d-flex justify-content-between align-items-center">
                      <span class="fw-semibold text-truncate small" :class="{ 'text-white': activeDocId === doc.id, 'text-dark': activeDocId !== doc.id }">
                        {{ doc.title }}
                      </span>
                    </div>
                    <p class="small mb-0 text-truncate" :class="{ 'text-white-50': activeDocId === doc.id, 'text-muted': activeDocId !== doc.id }" style="font-size: 0.75rem;">
                      {{ doc.description }}
                    </p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Center & Right: Document Viewer + Outline -->
      <div class="col-12 col-lg-9">
        <!-- Document Content Card -->
        <div class="card shadow-sm border-0">
          <!-- Loading State -->
          <div v-if="loadingContent" class="card-body text-center py-5">
            <div class="spinner-border text-primary mb-3" role="status"></div>
            <h6 class="text-muted">Loading documentation...</h6>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="card-body py-5 text-center">
            <div class="alert alert-danger d-inline-block text-start p-4 shadow-sm" style="max-width: 600px;">
              <h5 class="alert-heading"><i class="fa fa-triangle-exclamation me-2"></i>Failed to Load Document</h5>
              <p class="mb-2">{{ error }}</p>
              <hr>
              <button class="btn btn-outline-danger btn-sm" @click="fetchDocContent(activeDocId)">
                <i class="fa fa-rotate me-1"></i>Try Again
              </button>
            </div>
          </div>

          <!-- Loaded Document -->
          <div v-else-if="currentDoc" class="card-body p-4">
            <!-- Document Meta Bar -->
            <div class="border-bottom pb-3 mb-4">
              <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                <div class="d-flex align-items-center gap-2">
                  <span class="badge bg-primary-subtle text-primary border border-primary-subtle px-2 py-1">
                    <i class="fa me-1" :class="currentDoc.icon || 'fa-book'"></i>{{ currentDoc.category }}
                  </span>
                  <span v-if="currentDoc.badge" class="badge bg-secondary-subtle text-secondary border border-secondary-subtle px-2 py-1">
                    {{ currentDoc.badge }}
                  </span>
                </div>
                <div class="d-flex align-items-center gap-2">
                  <button class="btn btn-outline-secondary btn-sm" @click="copyRawMarkdown" title="Copy raw markdown to clipboard">
                    <i class="fa me-1" :class="copied ? 'fa-check text-success' : 'fa-copy'"></i>
                    {{ copied ? 'Copied!' : 'Copy Markdown' }}
                  </button>
                  <button class="btn btn-outline-secondary btn-sm" @click="downloadMarkdown" title="Download markdown file">
                    <i class="fa fa-download me-1"></i>Download
                  </button>
                  <button class="btn btn-outline-primary btn-sm d-xl-none" @click="showOutlineMobile = !showOutlineMobile">
                    <i class="fa fa-list-ol me-1"></i>Outline
                  </button>
                </div>
              </div>

              <h2 class="text-dark fw-bold mb-2">{{ currentDoc.title }}</h2>
              <p class="text-muted mb-3" style="font-size: 0.95rem;">{{ currentDoc.description }}</p>

              <div class="d-flex flex-wrap align-items-center gap-3 text-muted small">
                <span><i class="fa fa-clock me-1 text-secondary"></i>~{{ currentDoc.readTimeMinutes }} min read ({{ currentDoc.wordCount?.toLocaleString() }} words)</span>
                <span><i class="fa fa-calendar-day me-1 text-secondary"></i>Updated: {{ formatDateTime(currentDoc.lastModified) }}</span>
                <span><i class="fa fa-file-code me-1 text-secondary"></i>Size: {{ formatSize(currentDoc.sizeBytes) }}</span>
              </div>
            </div>

            <!-- Content Grid: Rendered Markdown + Sticky Table of Contents Outline -->
            <div class="row g-4">
              <!-- Rendered Markdown Body -->
              <div class="col-12 col-xl-9">
                <div class="rendered-markdown-content" v-html="renderedHtml" @click="handleContentClick"></div>
                
                <div class="border-top pt-4 mt-5 d-flex justify-content-between align-items-center text-muted small">
                  <span>Document ID: <code>{{ currentDoc.id }}</code></span>
                  <button class="btn btn-outline-secondary btn-sm" @click="scrollToTop">
                    <i class="fa fa-arrow-up me-1"></i>Back to Top
                  </button>
                </div>
              </div>

              <!-- Right Sticky Outline (On This Page) -->
              <div class="col-12 col-xl-3 d-none d-xl-block">
                <div class="sticky-top doc-outline-card p-3 bg-light rounded border" style="top: 80px;">
                  <h6 class="fw-bold text-dark text-uppercase small mb-3" style="letter-spacing: 0.5px;">
                    <i class="fa fa-list me-2 text-primary"></i>On This Page
                  </h6>
                  <div v-if="currentDoc.outline && currentDoc.outline.length" class="doc-outline-list overflow-auto" style="max-height: calc(100vh - 220px);">
                    <ul class="list-unstyled mb-0">
                      <li
                        v-for="(item, idx) in currentDoc.outline"
                        :key="idx"
                        class="mb-1"
                        :class="`ps-${(item.level - 1) * 2}`"
                      >
                        <a
                          :href="`#${item.slug}`"
                          class="doc-outline-link text-decoration-none text-muted d-block text-truncate py-1"
                          :class="{ 'fw-bold text-primary': activeSlug === item.slug }"
                          :title="item.text"
                          @click.prevent="scrollToSlug(item.slug)"
                        >
                          <i v-if="item.level === 1" class="fa fa-chevron-right small me-1 text-primary" style="font-size: 0.65rem;"></i>
                          <i v-else-if="item.level === 2" class="fa fa-minus small me-1 text-secondary" style="font-size: 0.65rem;"></i>
                          <i v-else class="fa fa-circle small me-1 text-muted" style="font-size: 0.45rem;"></i>
                          {{ item.text }}
                        </a>
                      </li>
                    </ul>
                  </div>
                  <div v-else class="text-muted small">
                    No section headings.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchWrapper } from '@/helpers/fetch-wrapper';
import { renderMarkdown } from '@/helpers/markdown';
import moment from 'moment';

export default {
  name: 'DocumentationViewer',
  data() {
    return {
      docsList: [],
      currentDoc: null,
      activeDocId: 'order-system',
      searchQuery: '',
      loadingList: false,
      loadingContent: false,
      error: null,
      copied: false,
      activeSlug: '',
      sidebarDrawer: false,
      showOutlineMobile: false,
    };
  },
  computed: {
    renderedHtml() {
      if (!this.currentDoc?.content) return '';
      return renderMarkdown(this.currentDoc.content);
    },
    filteredGroupedDocs() {
      const q = this.searchQuery.toLowerCase().trim();
      const filtered = this.docsList.filter((doc) => {
        if (!q) return true;
        return (
          doc.title.toLowerCase().includes(q) ||
          doc.description.toLowerCase().includes(q) ||
          doc.category.toLowerCase().includes(q) ||
          doc.id.toLowerCase().includes(q)
        );
      });

      const groupsMap = {};
      for (const doc of filtered) {
        const cat = doc.category || 'General';
        if (!groupsMap[cat]) {
          groupsMap[cat] = { category: cat, docs: [] };
        }
        groupsMap[cat].docs.push(doc);
      }

      return Object.values(groupsMap);
    },
  },
  watch: {
    '$route.params.docId': {
      immediate: true,
      handler(newId) {
        if (newId && newId !== this.activeDocId) {
          this.activeDocId = newId;
          this.fetchDocContent(newId);
        }
      },
    },
  },
  mounted() {
    this.fetchDocsList();
    const docIdParam = this.$route.params.docId || 'order-system';
    this.activeDocId = docIdParam;
    this.fetchDocContent(docIdParam);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    async fetchDocsList() {
      this.loadingList = true;
      try {
        const res = await fetchWrapper.get('api/docs');
        if (res && res.success) {
          this.docsList = res.data || [];
        }
      } catch (err) {
        console.error('Failed to load docs list:', err);
      } finally {
        this.loadingList = false;
      }
    },

    async fetchDocContent(docId) {
      if (!docId) return;
      this.loadingContent = true;
      this.error = null;
      this.sidebarDrawer = false;

      try {
        const res = await fetchWrapper.get(`api/docs/${docId}`);
        if (res && res.success) {
          this.currentDoc = res.data;
          this.activeDocId = docId;
          this.scrollToTop();
        } else {
          this.error = res?.message || 'Failed to load document content.';
        }
      } catch (err) {
        console.error(`Failed to load document ${docId}:`, err);
        this.error = typeof err === 'string' ? err : err?.message || 'Failed to load document.';
      } finally {
        this.loadingContent = false;
      }
    },

    selectDocument(docId) {
      if (this.activeDocId === docId) return;
      this.activeDocId = docId;
      this.$router.push(`/docs/${docId}`);
    },

    copyRawMarkdown() {
      if (!this.currentDoc?.content) return;
      navigator.clipboard.writeText(this.currentDoc.content).then(() => {
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2500);
      });
    },

    downloadMarkdown() {
      if (!this.currentDoc?.content) return;
      const blob = new Blob([this.currentDoc.content], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${this.currentDoc.id}.md`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },

    scrollToSlug(slug) {
      this.activeSlug = slug;
      const el = document.getElementById(slug);
      if (el) {
        const top = el.getBoundingClientRect().top + window.pageYOffset - 90;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    },

    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    handleContentClick(event) {
      const copyBtn = event.target.closest('.doc-copy-btn');
      if (copyBtn) {
        const code = decodeURIComponent(copyBtn.getAttribute('data-copy-code') || '');
        navigator.clipboard.writeText(code).then(() => {
          copyBtn.innerText = 'Copied!';
          setTimeout(() => {
            copyBtn.innerHTML = '<i class="fa fa-copy me-1"></i>Copy';
          }, 2000);
        });
        return;
      }

      const target = event.target.closest('a');
      if (!target) return;

      const href = target.getAttribute('href');
      if (href && href.startsWith('#')) {
        event.preventDefault();
        const slug = href.substring(1);
        this.scrollToSlug(slug);
      }
    },

    handleScroll() {
      if (!this.currentDoc?.outline?.length) return;
      const headings = this.currentDoc.outline
        .map((item) => document.getElementById(item.slug))
        .filter(Boolean);

      const scrollPosition = window.scrollY + 120;

      for (let i = headings.length - 1; i >= 0; i--) {
        const heading = headings[i];
        if (heading.offsetTop <= scrollPosition) {
          this.activeSlug = heading.id;
          return;
        }
      }
    },

    formatDateTime(dateStr) {
      if (!dateStr) return 'N/A';
      return moment(dateStr).format('YYYY-MM-DD HH:mm IST');
    },

    formatSize(bytes) {
      if (!bytes) return '0 KB';
      const kb = bytes / 1024;
      return kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb.toFixed(1)} KB`;
    },
  },
};
</script>

<style scoped>
.documentation-page {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.active-doc {
  border-left: 4px solid #1d4ed8 !important;
}

.doc-outline-link {
  font-size: 0.825rem;
  transition: color 0.15s ease-in-out;
}

.doc-outline-link:hover {
  color: #2563eb !important;
}

.doc-outline-card {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

:deep(.doc-heading:hover .doc-anchor) {
  opacity: 1 !important;
}

:deep(.doc-heading) {
  scroll-margin-top: 90px;
}

:deep(.rendered-markdown-content) {
  color: #1f2937;
  font-size: 0.95rem;
  line-height: 1.7;
}

:deep(.rendered-markdown-content p) {
  margin-bottom: 1rem;
}

:deep(.rendered-markdown-content code) {
  font-size: 0.875em;
}

:deep(.rendered-markdown-content table) {
  width: 100%;
}
</style>

